import { storeToRefs } from 'pinia';
import { useEventListener } from '@vueuse/core';
import * as Sentry from '@sentry/vue';
import { useVersionStore } from '@/stores/useVersionStore';
import { API_VERSION_HEADER, APP_VERSION, CLIENT_VERSION_HEADER } from '@/constants/version';

export function useVersion() {
  const versionStore = useVersionStore();
  const { apiVersion, noticeDismissed } = storeToRefs(versionStore);

  function reportApiVersion(version: string | undefined | null) {
    if (!version || version === apiVersion.value) return;

    apiVersion.value = version;
    noticeDismissed.value = false;
    Sentry.setTag('api_version', version);
  }

  async function fetchApiVersion() {
    if (import.meta.env.VITE_STATIC === 'true') return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/version`, {
        headers: { [CLIENT_VERSION_HEADER]: APP_VERSION },
        cache: 'no-store',
      });

      const header = response.headers.get(API_VERSION_HEADER);
      if (header) {
        reportApiVersion(header);
        return;
      }

      const body: { version?: string } = await response.json();
      reportApiVersion(body.version);
    } catch {
      // no-op
    }
  }

  function syncApiVersion() {
    fetchApiVersion();
    useEventListener(document, 'visibilitychange', () => {
      if (document.visibilityState === 'visible') fetchApiVersion();
    });
  }

  function reload() {
    window.location.reload();
  }

  return { reportApiVersion, fetchApiVersion, syncApiVersion, reload };
}
